/**
 * ProtectedRoute Component
 * Wrapper component that protects routes requiring authentication and permissions
 * Redirects to login if user is not authenticated
 * Redirects to dashboard if user lacks required permissions
 */

import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated } from '../../services/authService';
import { useAbility } from '../../hooks/useAbility';
import { Loader } from 'lucide-react';
import './ProtectedRoute.css';

const ProtectedRoute = ({ children, requiredPermission }) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const { ability, loading: abilityLoading } = useAbility();
  const location = useLocation();

  // Public routes that don't require authentication
  const publicRoutes = React.useMemo(() => [
    '/login', '/logout', '/forgot-password', '/reset-password', 
    '/auth/onedrive/callback', '/auth/m365-email/callback'
  ], []);

  useEffect(() => {
    const checkAuth = async () => {
      // Skip auth check for public routes
      if (publicRoutes.includes(location.pathname)) {
        setLoading(false);
        return;
      }

      const isAuth = await isAuthenticated();
      setAuthenticated(isAuth);
      setLoading(false);
    };

    checkAuth();
  }, [location.pathname, publicRoutes]);

  if (loading || abilityLoading) {
    return (
      <div className="protected-route-loading">
        <div className="protected-route-spinner">
          <Loader size={40} />
        </div>
        <p>Verificando autenticación...</p>
      </div>
    );
  }

  // Allow access to public routes without authentication
  if (publicRoutes.includes(location.pathname)) {
    return children;
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermission) {
    const { action, subject } = requiredPermission;
    if (!ability.can(action, subject)) {
      return <Navigate to="/dashboard" state={{ from: location, error: 'No tienes permisos para acceder a esta página' }} replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
